import { NullOrUndefined, anyReallyAny } from "./any.tools";

/**
 * Return a number as a string with 2 digits minimum
 * 
 * @param i The number
 * @returns the number as a string with 2 digits minimum
 */
export function twoDigits(i: number): string {
    if (i < 0) {
        return "-" + twoDigits(-i);
    }
    return i < 10 ? "0" + i : "" + i;
}

/**
 * Return a boolean, check if value is a number type
 * 
 * @param value to check
 */
export function isNumber(value: anyReallyAny): value is number {
    return typeof value === "number" && isNumeric(value);
}

/**
 * @returns a number if value is numeric, null otherwise
 */
export function asNumber(value: anyReallyAny): number | null {
    return isNumeric(value) ? Number(value) : null;
}

/**
 * Return a boolean, check if value is a numeric value or not
 * !This does not ensure value is a number, you should probably do Number(value) after this check.
 * 
 * @param value to check
 */
export function isNumeric(value: anyReallyAny): boolean {
    const valueType = typeof value;
    return (valueType === "number" || valueType === "string") && value as unknown !== "" && isFinite(value as unknown as number);
}

/**
 * Return a boolean, check if value is a number and is an integer
 * ! value must be a number
 *
 * @param value Value to check
 * @returns {*}  {boolean}
 */
export function isInteger(value: anyReallyAny): value is number {
    if (isNumber(value)) {
        return Math.floor(value) === value;
    }
    return false;
}

/**
 * Return a boolean, check if value is an integer or not 
 * ! value may be a string
 *
 * @param value Value to check
 * @returns {*}  {boolean}
 */
export function isNumericInteger(value: anyReallyAny): boolean {
    if (isNumeric(value)) {
        value = Number(value);
        return Math.floor(value) === value;
    }
    return false;
}

/**
 * @returns a number if value is an integer, null otherwise
 */
export function asInteger(value: anyReallyAny): number | null {
    return isNumericInteger(value) ? Number(value) : null;
}

/**
 * @returns {boolean} true if value is invalid (null or NaN value or Infinity or not a number)
 */
export function isInvalid<N = number>(value: N | NullOrUndefined): boolean {
    return !isNumeric(value as number | string | NullOrUndefined);
}

/**
 * @returns {boolean} true if value will produce a valid number with Number(value)
 * It is different from isNumeric because isNumeric(new Date()) => false canBeCastToNumber(new Date()) => true
 */
export function canBeCastToNumber<N = number>(value: N | NullOrUndefined): boolean {
    return value != null && value as unknown !== "" && isFinite(value as unknown as number);
}

/**
 * Return the lowest of the given numbers
 * @param list List of numbers
 * @returns {UnixTimestamp | null}
 */
export function minimum<N = number>(...list: (N | NullOrUndefined)[]): N | null {
    let result = null;
    if (list) {
        for (const value of list) {
            // we don't use Math.min(...) directly because we handle invalid values differently
            result = min(result, value);
        }
    }
    return result;
}

/**
 * Returns the lowest. If one instant is invalid, will return the other.
 * @param a First number to compare
 * @param b Second number to compare
 * @returns {UnixTimestamp | null}
 */
export function min<N = number>(a: N | NullOrUndefined, b: N | NullOrUndefined): N | null {
    const invA = !canBeCastToNumber(a);
    const invB = !canBeCastToNumber(b);
    if (invA && invB) {
        return null;
    } else if (invA) {
        return b ?? null;
    } else if (invB) {
        return a ?? null;
    } else if (a != null && b != null && a <= b) {
        return a ?? null;
    } else {
        return b ?? null;
    }
}

/**
 * Return the greatest of the given numbers
 * @param list List of numbers
 * @returns {N | null}
 */
export function maximum<N = number>(...list: (N | NullOrUndefined)[]): N | null {
    let result = null;
    if (list) {
        for (const n of list) {
            // we don't use Math.max(...) directly because we handle invalid values differently
            result = max(result, n);
        }
    }
    return result;
}

/**
 * Returns the greatest. If one instant is invalid, will return the other.
 * @param a First number to compare
 * @param b Second number to compare
 * @returns {N | null}
 */
export function max<N = number>(a: N | NullOrUndefined, b: N | NullOrUndefined): N | null {
    const invA = !canBeCastToNumber(a);
    const invB = !canBeCastToNumber(b);
    if (invA && invB) {
        return null;
    } else if (invA) {
        return b ?? null;
    } else if (invB) {
        return a ?? null;
    } else if (a != null && b != null && a >= b) {
        return a;
    } else {
        return b ?? null;
    }
}

// ----- Intervals ----------------------------------------

/**
 * strict means if 2 intervals are exactly after each other they don't intersect
 * if begin or end is null we consider it is -/+ infinite
 * @returns true if there is a "not unique" intersection  between the 2 intervals
 */
export function intersectStrict<N = number>(interval1Begin: N | NullOrUndefined, interval1End: N | NullOrUndefined, interval2Begin: number | NullOrUndefined, interval2End: number | NullOrUndefined): boolean {
    return intersectSingleStrict(interval1Begin ?? Number.NEGATIVE_INFINITY, interval1End ?? Number.POSITIVE_INFINITY, interval2Begin ?? Number.NEGATIVE_INFINITY, interval2End ?? Number.POSITIVE_INFINITY) ||
        intersectSingleStrict(interval2Begin ?? Number.NEGATIVE_INFINITY, interval2End ?? Number.POSITIVE_INFINITY, interval1Begin ?? Number.NEGATIVE_INFINITY, interval1End ?? Number.POSITIVE_INFINITY);
}

/**
 * strict means if 2 intervals are exactly after each other they don't intersect
 * @returns true if the begin or the end of the interval2 is in the interval1
 */
function intersectSingleStrict<N = number>(interval1Begin: N, interval1End: N, interval2Begin: N, interval2End: N): boolean {
    if (interval1Begin < interval1End && interval2Begin < interval2End && interval1End === interval2Begin) {
        return false;
    }
    if (interval1Begin < interval1End && interval2Begin < interval2End && interval2End === interval1Begin) {
        return false;
    }
    if (isBetween(interval2Begin, interval1Begin, interval1End, true)) {
        return true;
    }
    if (isBetween(interval2End, interval1Begin, interval1End, true)) {
        return true;
    }
    return false;
}

/**
 * if begin or end is null we consider it is -/+ infinite
 * @returns true if there is an intersection (even unique) between the 2 intervals
 */
export function intersect(interval1Begin: number | NullOrUndefined, interval1End: number | NullOrUndefined, interval2Begin: number | NullOrUndefined, interval2End: number | NullOrUndefined): boolean {
    return intersectSingle(interval1Begin ?? Number.NEGATIVE_INFINITY, interval1End ?? Number.POSITIVE_INFINITY, interval2Begin ?? Number.NEGATIVE_INFINITY, interval2End ?? Number.POSITIVE_INFINITY) ||
        intersectSingle(interval2Begin ?? Number.NEGATIVE_INFINITY, interval2End ?? Number.POSITIVE_INFINITY, interval1Begin ?? Number.NEGATIVE_INFINITY, interval1End ?? Number.POSITIVE_INFINITY);
}

/**
 * @returns true if the begin or the end of the interval2 is in the interval1
 */
function intersectSingle<N = number>(interval1Begin: N, interval1End: N, interval2Begin: N, interval2End: N): boolean {

    if (isBetween(interval2Begin, interval1Begin, interval1End, true)) {
        return true;
    }
    if (isBetween(interval2End, interval1Begin, interval1End, true)) {
        return true;
    }
    return false;
}

/**
 * @param toTest number we want to compare
 * @param first start of interval
 * @param second finish of interval
 * @param includeBounds if true, return true if toTest equals first or second
 * 
 * if first or second is null we consider it is -/+ infinite
 * @returns true if the toTest time is between the first and second times also returns true if toTest equals first or second and includeBounds is true
 */
export function isBetween<N = number>(toTest: N, first: N | NullOrUndefined, second: N | NullOrUndefined, includeBounds: boolean): boolean {

    // Infinite boundaries, no need to check
    if (first == null && second == null) { return true; }

    if (first == null) {// Infinite negative value
        first = Number.NEGATIVE_INFINITY as unknown as N;
    }
    if (second == null) {// Infinite positive value
        second = Number.POSITIVE_INFINITY as unknown as N;
    }

    if (includeBounds) {
        if (first <= toTest && toTest <= second) {
            return true;
        }
        if (second <= toTest && toTest <= first) {
            return true;
        }
    } else {
        if (first < toTest && toTest < second) {
            return true;
        }
        if (second < toTest && toTest < first) {
            return true;
        }
    }

    return false;
}


/**
 * Tests if toTest is between the first and second times also returns true if toTest equals first but not second.
 * If either first or second is null, will be interpreted as infinite.
 * @param toTest number we want to compare
 * @param first start of interval. If null we will just check toTest <= second
 * @param second finish of interval. If null we will just check toTest >= first
 * @returns true if first <= toTest < second
 */
export function isInside<N = number>(toTest: N, first: N | NullOrUndefined, second: N | NullOrUndefined): boolean {
    if (first != null && toTest < first) {
        return false;
    }
    if (second != null && second <= toTest) {
        return false;
    }
    return true;
}

/**
 * if strict is true : if the intersection is a single instant getIntersection will return null
 * @returns [begin, end] of intersection or null if no intersection
 *
 * ex : (1,3) and (2,4) => (2,3)
 * ex : (1,2) and (2,3) => null if strict, (2,2) otherwise
 */
export function getIntersection<N = number>(interval1Begin: N, interval1End: N, interval2Begin: N, interval2End: N, strict: boolean): { begin: N, end: N } | null {
    let begin: N, end: N;
    if (isBetween(interval2Begin, interval1Begin, interval1End, true) || (interval1Begin == null && interval2Begin == null)) {
        begin = interval2Begin;
    } else if (isBetween(interval1Begin, interval2Begin, interval2End, true)) {
        begin = interval1Begin;
    } else {
        return null;
    }

    if (isBetween(interval2End, interval1Begin, interval1End, true) || (interval1End == null && interval2End == null)) {
        end = interval2End;
    } else if (isBetween(interval1End, interval2Begin, interval2End, true)) {
        end = interval1End;
    } else {
        return null;
    }

    if (strict && begin === end) {
        return null;
    }

    return { begin, end };
}

/**
 * Returns the arithmetic mean (average) value from a number array. If one value is invalid, it will not be computed. If no instant is valid, null is returned
 * @returns {*} {(N | null)} mean of all valid number
 */
export function mean<N = number>(values: N[]): N | null {
    let totalTsValue = 0;
    let totalTsComputed = 0;

    for (const value of values) {
        if (canBeCastToNumber(value)) {
            totalTsValue += Number(value);
            totalTsComputed++;
        }
    }

    if (totalTsComputed !== 0) {
        return (totalTsValue / totalTsComputed) as unknown as N;
    } else {
        return null;
    }
}

/** 
 * @returns -1 if nLeft < nRight, 1 if nLeft > nRight, 0 if nLeft == nRight
 */
export function compareAsc(nLeft: number, nRight: number): number {
    const diff = nLeft - nRight;

    if (diff < 0) {
        return -1;
    } else if (diff > 0) {
        return 1;
    } else {
        // Return 0 if diff is 0; return NaN if diff is NaN
        return diff;
    }
}

/**
 * @returns -1 if nLeft > nRight, 1 if nLeft < nRight, 0 if nLeft == nRight
 */
export function compareDesc(nLeft: number, nRight: number): number {
    const diff = nLeft - nRight;

    if (diff > 0) {
        return -1;
    } else if (diff < 0) {
        return 1;
    } else {
        // Return 0 if diff is 0; return NaN if diff is NaN
        return diff;
    }
}

/**
 * Find the valid number in values that is the closest to approximate
 */
export function closest(approximate: number, values: number[]): number | null {
    if (!canBeCastToNumber(approximate)) { return null; }

    let resultValue: number | null = null;
    let resultDiff: number | null = null;
    for (let i = 0; i < values.length; i++) {
        const v = values[i];
        if (!canBeCastToNumber(v)) { continue; }
        const diff = Math.abs(v - approximate);
        if (resultDiff == null || diff < resultDiff) {
            resultValue = v;
            resultDiff = diff;
        }
    }
    return resultValue;
}

/**
 * Convert a value to px string
 * ex: toPx(125.04) => "125.04px"
 */
export function toPx(value: number): string {
    return value + "px";
}

/**
 * Convert a pc string value to number value
 * ex: fromPx("125.04px") => 125.04"
 */
export function fromPx(stringValue: string): number {
    return parseFloat(stringValue);
}


/** Used by IntRange type */
type Enumerate<N extends number, Acc extends number[] = []> = Acc["length"] extends N
    ? Acc[number]
    : Enumerate<N, [...Acc, Acc["length"]]>
/** 
 * Int range value typing
 * @see https://stackoverflow.com/questions/39494689/is-it-possible-to-restrict-number-to-a-certain-range
 */
export type IntRange<F extends number, T extends number> = Exclude<Enumerate<T>, Enumerate<F>>
